import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client'
import jsSHA from 'jssha'
import gql from 'graphql-tag'
import { SubscriptionClient } from 'subscriptions-transport-ws'
import _, {
  get,
  chain,
  isArray,
  values,
  merge,
  range,
  some,
  sortBy,
  omit,
  cloneDeep,
} from 'lodash'
import { useEffect, useState } from 'react'
import { singular } from 'pluralize'
import { DateTime } from 'luxon'

import {
  storageMode,
  hasMultiServiceRequests,
  trackAvailabilitySearch,
  trackAvailabilitySearchResultFound,
  trackAvailabilitySearchResultNotFound,
  trackAvailabilitySearchResultNoAddonsFound,
  trackAvailabilitySearchAlternateTimeFound,
  trackAvailabilitySearchWithoutServiceFound,
  trackAvailabilitySearchAlternateServiceFound,
  trackAvailabilitySearchError,
  notifyBugsnag
} from '../helpers'
import { resolve } from 'bluebird'
import {
  PRODUCTS,
  ADDRESSES,
  PAYMENT_METHODS,
  ME,
  LOAD_USER,
  UPDATE_USER,
  CREATE_TRANSACTION,
  APPOINTMENT_DETAILS,
  APPOINTMENT_SERVICE_DETAILS,
  LOAD_MARKETS,
  SERVICE_TYPES,
  LOAD_SERVICES,
  LOAD_MARKET_SERVICES_WITH_ADD_ONS,
  CREATE_ADDRESS,
  UPDATE_ADDRESS,
  DESTROY_ADDRESS,
  CREATE_PAYMENT_METHOD,
  CREATE_PAYMENT_INTENT,
  DESTROY_PAYMENT_METHOD,
  LOAD_MEMBERSHIPS,
  LOAD_PACKAGES,
  LOAD_GIFT_CARD,
  LOAD_USER_APPOINTMENTS,
  UPDATE_APPOINTMENT,
  UPDATE_APPOINTMENT_SERVICE,
  DESTROY_APPOINTMENT_SERVICE,
  LOAD_CLIENT_SURVEY_QUESTIONS,
  CREATE_SURVEY_ANSWER,
  LOAD_APPOINTMENTS,
  FETCH_PRODUCT,
  GET_BANNER,
  UPDATE_MEMBERSHIP,
  UPDATE_SURVEY_ANSWER,
  CALCULATE_APPOINTMENT_PRICE,
  LOAD_DETAIL_SERVICES,
  APPOINTMENT_SERVICE_INPUT_FIELDS,
  FULL_DAY_AVAILABILITY
} from './GraphQlQueries'
import { LOAD_SERVICES_FOR_MARKET } from '.'
import {
  buildFullDayAvailabilityVariables,
  dayViewSlotToSelectedAppointment
} from '../helpers/dayViewAvailability'
import moment from 'moment-timezone'

const STARBASE_URL = process.env.NEXT_PUBLIC_STARBASE_URL
const CLIENT_APPLICATION_ID = process.env.NEXT_PUBLIC_CLIENT_ID
const CLIENT_SECRET = process.env.NEXT_PUBLIC_CLIENT_SECRET

/**
 * retrieve the currently logged in user
 */
export const me = () =>
  getAccessToken() &&
  runQuery({
    query: ME,
  }).then(({ data: { me } }) => me)

/**
 * load user if available
 *
 * @param {*} [deps=[]]
 */
export const useMe = (deps = []) =>
  useStateEffect(me, undefined, [...deps, getAccessToken()])

export const signRequestByClient = async (method, endpoint, body) => {
  const sha = new jsSHA('SHA-256', 'TEXT')
  sha.setHMACKey(CLIENT_SECRET, 'TEXT')
  sha.update([method, endpoint, JSON.stringify(body)].join('|'))
  const hmac = sha.getHMAC('B64')

  return 'Glam ' + [CLIENT_APPLICATION_ID, hmac].join(':');
}

/**
 * buildAuthHeader: builds the authorization header for a request
 *
 * uses accessToken if present, otherwise sign w/ client
 *
 * @param {*} accessToken
 * @param {*} path
 * @param {*} body
 * @return {*}
 */
const buildAuthHeader = async (accessToken, path, body) => {
  if (accessToken) {
    return `Bearer ${accessToken}`
  }

  return signRequestByClient('POST', path, body)
}

/**
 * availabilityResultsFrom: safely read the results array out of an availability response body.
 *
 * Only success responses carry `data`; error bodies carry `errors` instead. Reading
 * `body.data.length` directly threw "Cannot read properties of undefined (reading 'length')"
 * whenever an error body reached one of those checks, and the booking flow then rendered that
 * raw TypeError message to the customer as if it were an availability message.
 *
 * @param {*} body parsed availability response body
 * @returns {Array} the results, or an empty array when the body carries none
 */
const availabilityResultsFrom = body => (Array.isArray(body?.data) ? body.data : [])

/**
 * isSuccessStatus: true for a 2xx response status
 *
 * @param {number} status
 * @returns {boolean}
 */
const isSuccessStatus = status => status >= 200 && status < 300

/**
 * userFacingError: marks an error whose message is safe to show to a customer.
 *
 * Availability errors are surfaced in the booking flow's status line, so the UI needs to know
 * which messages came from the server (a real explanation, e.g. "that service isn't available
 * at this time") and which are unexpected bugs whose raw text must never be shown.
 *
 * @param {Error} error
 * @returns {Error} the same error, flagged
 */
const userFacingError = error => {
  error.isUserFacing = true
  return error
}

/**
 * appointmentAvailabilitySearch: availability search
 *
 * @param {*} actualStartDateTime
 * @param {*} address
 * @param {*} serviceRequests
 * @returns
 */
export const appointmentAvailabilitySearch = async (
  dispatch,
  actualStartDateTime,
  timezone,
  timeVariance,
  timeInterval,
  address,
  serviceRequests,
  providerFilter,
  marketServices
) => {
  // check if appointment is multiple services— if initial request fails,
  // we can query these separately
  const hasMultipleServices = hasMultiServiceRequests(serviceRequests)

  // check if appointment services have any addon services
  const hasAddonServices = some(serviceRequests, (request) => request.addOnServiceIds.length)

  dispatch({
    selectedAppointment: false
  })

  actualStartDateTime =
    DateTime.fromJSDate(actualStartDateTime).setZone(timezone)

  if (
    !(
      actualStartDateTime &&
      actualStartDateTime > new Date() &&
      actualStartDateTime.hour !== 0 &&
      address &&
      serviceRequests &&
      serviceRequests.length
    )
  ) {
    return null
  }

  // track only once we know we are actually going to search. this used to be the first
  // statement in the function, which fired an `Availability Search` for every step-3 mount
  // and every date picked before a time — none of which hit the network. it also ran before
  // the market-timezone conversion above, so the tracked `startDateTime` was serialized in
  // the browser's zone while every outcome event below used the market's.
  trackAvailabilitySearch(actualStartDateTime, serviceRequests, marketServices, address)

  // for simulataneous (not back-to-back) services, send an extra service request for each
  serviceRequests = serviceRequests.flatMap(sr => {
    if (sr.backToBack) {
      return sr
    }

    // include a duplicate service request for each instance requested
    return range(0, sr.instances).map(() => merge({}, sr, { instances: 1 }))
  })

  // build request body
  const body = {
    address,
    actualStartDateTime: actualStartDateTime.toSeconds(),
    services: serviceRequests,
    timeVariance,
    timeInterval,
    providerFilter
  }

  try {
    const path = '/api/v1/appointments/availability'

    // access token if we have on
    const accessToken = getAccessToken()

    const res = await fetch(
      STARBASE_URL.concat(path),
      {
        method: 'POST',
        headers: {
          Authorization: await buildAuthHeader(accessToken, path, body),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body),
        timeout: 20000,
        responseType: 'json'
      }
    )

    // server error
    if (res.status > 499) {
      throw userFacingError(new Error('Error checking availability. Please try again.'))
    }

    const responseBody = await res.json()
    const retry = {
      noAddonSearch: [],
      singleServiceSearch: [],
      alternateServiceSearch: {
        message: null,
        serviceRequests: [],
        alternateServiceId: null,
      },
    }

    // if we received an error, check if it's an alternateService error. otherwise rethrow
    if (res.status > 299) {
      const error = merge(new Error(), res, responseBody)

      // check if this is the error that we're looking for
      const alternateServiceError = error?.errors?.find(error => error.type === 'alternateService' && error.value)

      // rethrow error if not
      if (!alternateServiceError) {
        throw userFacingError(error)
      }

      // replace the unavailable service with the alternate service
      const unavailableServiceId = alternateServiceError.path;
      const alternateServiceId = alternateServiceError.value;

      const alternateServiceRequests = serviceRequests.map(request => {
        if (request.serviceId === unavailableServiceId) {
          return { ...request, serviceId: alternateServiceId }
        }
        return request
      })

      // request again
      const alternateServiceBody = {
        ...body,
        services: alternateServiceRequests,
      }
      const alternateServiceResponse = await fetch(
        STARBASE_URL.concat(path),
        {
          method: 'POST',
          headers: {
            Authorization: await buildAuthHeader(accessToken, path, alternateServiceBody),
            'Content-Type': 'application/json'
          },

          body: JSON.stringify(alternateServiceBody),
          timeout: 20000,
          responseType: 'json'
        }
      )

      // makes sure we got a 200 response
      if (isSuccessStatus(alternateServiceResponse.status)) {
        const alternateResponseBody = await alternateServiceResponse.json()

        // return if we found some results
        if (availabilityResultsFrom(alternateResponseBody).length) {
          retry.alternateServiceSearch = {
            ...retry.alternateServiceSearch,
            message: alternateServiceError.message,
            alternateServiceId,
            serviceRequests: alternateServiceRequests,
          }

          trackAvailabilitySearchAlternateServiceFound(actualStartDateTime, retry.alternateServiceSearch.serviceRequests, marketServices, address)

          // return retry with empty results
          return {
            data: [],
            retry,
          }
        }
      }

      // the alternate service has no availability at this time either (or its search errored),
      // so there is nothing to offer the customer. surface the original server error, which
      // explains which service was unavailable.
      //
      // this branch used to fall through to the result handling below, which then read
      // `responseBody.data` — but `responseBody` is the original 4xx error body and has no
      // `data`, so it threw a TypeError that was rendered to the customer verbatim.
      throw userFacingError(error)
    }

    const results = availabilityResultsFrom(responseBody)

    // if we got results, return
    if (results.length) {
      const originalTimeSearch = actualStartDateTime.toSeconds()
      let isAlternateTime = false
      // check if result is an alternate time
      results.forEach(result => {
        if (result.actualStartDateTime !== originalTimeSearch) {
          isAlternateTime = true
        }
      })

      if (isAlternateTime) {
        trackAvailabilitySearchAlternateTimeFound(actualStartDateTime, serviceRequests, marketServices, address)
      } else {
        trackAvailabilitySearchResultFound(actualStartDateTime, serviceRequests, marketServices, address)
      }

      return {
        data: results,
        retry
      }
    }

    if (hasAddonServices) {
      body.services = serviceRequests.map(request => ({ ...request, addOnServiceIds: [] }))

      const res = await fetch(
        STARBASE_URL.concat(path),
        {
          method: 'POST',
          headers: {
            Authorization: await buildAuthHeader(accessToken, path, body),
            'Content-Type': 'application/json'
          },

          body: JSON.stringify(body),
          timeout: 20000,
          responseType: 'json'
        }
      )

      // a failed retry just means we have no add-on-free alternative to offer, so read it
      // defensively: an error body carries no `data` and must not crash the whole search.
      const data = isSuccessStatus(res.status) ? await res.json() : null

      // if appointment is found for services w/o addons
      // return original service requets without addon ids from body
      if (availabilityResultsFrom(data).length) {
        retry.noAddonSearch = body.services
      }
    }

    if (hasMultipleServices) {
      // try services requests individually
      const singleServiceFetches = serviceRequests.map(s => ({
        address,
        actualStartDateTime: actualStartDateTime.toSeconds(),
        services: [{ ...s }],
        timeVariance,
        timeInterval,
        providerFilter
      }))

      const promises = singleServiceFetches.map(async s => fetch(
        STARBASE_URL.concat(path),
        {
          method: 'POST',
          headers: {
            Authorization: await buildAuthHeader(accessToken, path, s),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(s),
          timeout: 20000,
          responseType: 'json'
        }
      ))

      const singleServiceResults = (await Promise.all((await Promise.all(promises)).map(r => r.json()))).map(availabilityResultsFrom)

      // filter original requests based on results. read the nested appointment services
      // defensively — a result without them is not a match, not a crash.
      const validServiceRequests = serviceRequests.filter(({ serviceId }) => (
        singleServiceResults.find((res) => res[0]?.appointmentServices?.[0]?.serviceId === serviceId)
      ))

      // return retry array if partial availability
      // ignore if > 2 services are unavailable
      if (
        validServiceRequests.length &&
        validServiceRequests.length < serviceRequests.length &&
        (serviceRequests.length - validServiceRequests.length) < 3
      ) {
        retry.singleServiceSearch = validServiceRequests
      }
    }

    // no results found tracking — only fire when nothing at all was found:
    // no direct result AND no add-on retry, single-service retry, or alternate-service retry.
    // (was `&&`, which fired Not Found unless ALL were present — i.e. nearly always,
    // double-counting partial-success searches as no-availability.)
    if (!(results.length || retry.noAddonSearch.length || retry.singleServiceSearch.length || retry.alternateServiceSearch.serviceRequests.length)) {
      trackAvailabilitySearchResultNotFound(actualStartDateTime, serviceRequests, marketServices, address)
    }

    // tracking for retry without addons
    if (!(results.length) && retry.noAddonSearch.length) {
      trackAvailabilitySearchResultNoAddonsFound(actualStartDateTime, retry.noAddonSearch, marketServices, address)
    }

    if (!(results.length) && retry.singleServiceSearch.length) {
      trackAvailabilitySearchWithoutServiceFound(actualStartDateTime, retry.singleServiceSearch, marketServices, address)
    }

    return {
      data: results,
      retry
    }
  } catch (error) {
    // the booking flow swallows this error into component state to render it in the status
    // line, so nothing else reports it. record it here or it stays invisible: unexpected
    // errors (bugs) go to Bugsnag, and every failure gets an analytics event so error rate
    // is distinguishable from a customer who navigated away mid-search.
    trackAvailabilitySearchError(actualStartDateTime, serviceRequests, marketServices, address, {
      errorMessage: error?.message,
      isUserFacing: !!error?.isUserFacing
    })

    if (!error?.isUserFacing) {
      notifyBugsnag(error, {
        actualStartDateTime: actualStartDateTime?.toISO?.(),
        serviceRequests,
        zip: address?.zip
      }, 'Availability Search')
    }

    formatAndRethrowError(error);
  }
}

/**
 * useAppointmentAvailabilitySearch
 *
 * @param {*} params
 */
export const useAppointmentAvailabilitySearch = (...params) =>
  useStateEffectWithError(
    () => appointmentAvailabilitySearch(...params),
    undefined,
    params.map(JSON.stringify)
  )

/**
 * fullDayAvailabilitySearch: run the fullDayAvailability GraphQL query for one day and return the raw
 * slot array. Callers transform it with buildDayView.
 * @param {object} args { date:'YYYY-MM-DD', address, serviceRequests }
 * @returns {Promise<Array>} raw availability slots
 */
export const fullDayAvailabilitySearch = async ({ date, address, serviceRequests }) => {
  const variables = buildFullDayAvailabilityVariables({ date, address, serviceRequests })
  try {
    const { data } = await runQuery({ query: FULL_DAY_AVAILABILITY, variables })
    return data.fullDayAvailability
  } catch (error) {
    // The hook surfaces this into component state (which emits the Availability Search Error analytics
    // event, where the market context lives); record unexpected failures here too, mirroring the classic
    // search, so a full-day outage is visible in Bugsnag rather than only in the UI.
    if (!error?.isUserFacing) {
      notifyBugsnag(error, { date, serviceRequests, zip: address?.zip }, 'Full Day Availability Search')
    }
    throw error
  }
}

/**
 * useFullDayAvailability: React hook wrapping fullDayAvailabilitySearch in the repo's error-aware
 * effect. Re-runs whenever the serialized params change (day, address, cart).
 * @param {object} params { date, address, serviceRequests }
 * @returns {[Array|undefined, Error|undefined]}
 */
export const useFullDayAvailability = params =>
  useStateEffectWithError(() => fullDayAvailabilitySearch(params), undefined, [JSON.stringify(params)])

/**
 * Refresh a day-view selection with exactly its selected services and start time.
 * Group consecutive instances by pro without locking the refreshed search to that
 * pro. The original cart can include services/add-ons the shopper already dropped.
 * Returns a booking-flow appointment, or null if only a further reduction is free.
 */
export const refreshDayViewAppointment = async (appointment, address, timezone) => {
  const requests = new Map()
  for (const service of appointment.appointmentServices) {
    const key = JSON.stringify([service.providerId, service.baseServiceId])
    const request = requests.get(key) || {
      serviceId: service.baseServiceId,
      addOnServiceIds: [],
      instances: 0,
      backToBack: true
    }
    request.instances += 1
    // Repeated IDs describe how many consecutive instances keep each add-on.
    request.addOnServiceIds.push(...(service.addOnServiceIds || []))
    requests.set(key, request)
  }
  const start = DateTime.fromSeconds(appointment.actualStartDateTime, {
    zone: timezone
  })
  const slots = await fullDayAvailabilitySearch({
    date: start.toISODate(),
    address,
    serviceRequests: [...requests.values()]
  })
  const slot = slots.find(result =>
    DateTime.fromISO(result.startDateTime, { zone: timezone }).toSeconds() ===
    appointment.actualStartDateTime
  )
  // Never silently accept another partial match during checkout recovery.
  if (!slot?.appointment?.appointmentServices?.length) {
    return null
  }
  return dayViewSlotToSelectedAppointment(slot.appointment, start, {
    marketId: appointment.address?.market?.id,
    marketShortName: appointment.address?.market?.shortName,
    timezone,
    addressId: address.id
  })
}

/**
 * bookAppointment
 *
 * TODO: this is not GraphQL. convert once we have a starbase endpoint
 *
 * @param {*} appointment
 * @param {*} appointmentAddress
 */
export const bookAppointment = async (appointment, appointmentAddress) => {
  appointment = { ...appointment } // make a copy since we're modifying it

  //TODO remove address from appointment entirely. not used anymore
  appointment.address = {}
  // access token if we have on
  const accessToken = getAccessToken()

  if (!appointment.ownerId) {
    appointment.ownerId = (await me()).id
  }

  const address = await updateOrCreateAddress(
    appointment.ownerId,
    appointmentAddress
  )
  appointment.addressId = address.id

  return fetch(
    STARBASE_URL.concat(`/api/v1/users/${appointment.ownerId}/appointments`),
    {
      method: 'POST',
      headers: {
        Authorization: accessToken ? 'Bearer '.concat(accessToken) : undefined,
        'Content-Type': 'application/json'
      },

      body: JSON.stringify(appointment),
      timeout: 15000,
      responseType: 'json'
    }
  ).then(res =>
    res
      .json()
      .then(({ data, errors }) => {
        if (errors && errors.length) {
          const err = new Error()
          err.errors = errors
          throw err
        }

        return data
      })
      .catch(formatAndRethrowError)
  )
}

/**
 * claimUserCredit
 *
 * @param {*} userCredit
 */
export const claimUserCredit = async (userId, claimCode) => {
  // access token if we have on
  const accessToken = getAccessToken()

  return fetch(
    STARBASE_URL.concat(`/api/v1/users/${userId}/user-credits/claim`),
    {
      method: 'POST',
      headers: {
        Authorization: accessToken ? 'Bearer '.concat(accessToken) : undefined,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ claimCode }),
      responseType: 'json'
    }
  )
    .then(res => {
      return res.json().then(body => {
        const { data } = body
        if (data) {
          return data
        }
        throw body
      })
    })
    .catch(formatAndRethrowError)
}

/**
 * calcApptPrice: ask the server to price an appointment
 *
 * Never cached: the price depends on server-side state that isn't part of the
 * query variables (available user credits, membership, promotions), so an
 * identical call can legitimately return a different total. Caching this made a
 * promo/gift code applied on the checkout Appointment Summary appear to do
 * nothing, because the recalculation after claiming the credit replayed the
 * cached pre-credit response instead of hitting the server.
 *
 * The service objects arriving here come from the availability search and from
 * ME / LOAD_APPOINTMENTS, all of which carry server-owned fields (`price`,
 * `duration`, `customPrice`) that `AppointmentServiceInput` does not define for a
 * client role. Sending one fails GraphQL variable coercion and kills the operation
 * before any resolver runs, so they are filtered down to
 * APPOINTMENT_SERVICE_INPUT_FIELDS here — at the wire boundary, so callers can hand
 * over server objects untouched and don't have to know the input type's shape.
 *
 * @param {string} startDateTime ISO start time in the market's timezone
 * @param {object} address appointment address
 * @param {Array} appointmentServices services to price
 * @param {number} [tipRate=0.2] gratuity rate to include in the total
 * @returns {Promise<object|undefined>} priced appointment totals
 */
export const calcApptPrice = (
  startDateTime,
  address,
  appointmentServices,
  tipRate = 0.2
) => {
  return runQuery({
    query: CALCULATE_APPOINTMENT_PRICE,
    variables: {
      startDateTime,
      address,
      appointmentServices: _.map(appointmentServices, aps =>
        _.pick(aps, APPOINTMENT_SERVICE_INPUT_FIELDS)
      ),
      tipRate
    }
  }).then(({ data }) => data?.calculateAppointmentPrice?.appointment)
}

/**
 * updateOrCreateAddress
 *
 * @param {*} userId
 * @param {*} address
 * @returns
 */
export const updateOrCreateAddress = (userId, appointmentAddress) => {
  // TODO: optimize
  appointmentAddress.userId = userId

  if (appointmentAddress.id) {
    return getUserAddresses(userId)
      .filter(address => address.id === appointmentAddress.id)
      .then(([address]) => {
        if (address) {
          updateAddress(appointmentAddress.id, appointmentAddress)
          return appointmentAddress
        }
        return createAddress(appointmentAddress)
      })
  }
  return createAddress(appointmentAddress)
}

/**
 * createAddress
 *
 * @param {*} address
 */
export const createAddress = address =>
  runMutation({
    mutation: CREATE_ADDRESS,
    variables: {
      address: omit(address, ['__typename'])
    }
  }).then(({ data: { createAddress: address } }) => address)

/**
 * updateAddress
 *
 * @param {*} address
 */
export const updateAddress = (id, address) => {
  return runMutation({
    mutation: UPDATE_ADDRESS,
    variables: {
      id,
      address: omit(address, ['__typename'])
    }
  }).then(({ data: { updateAddress } }) => updateAddress)
}

export const destroyAddress = id =>
  runMutation({
    mutation: DESTROY_ADDRESS,
    variables: {
      id
    }
  }).then(({ data: { destroyAddress } }) => destroyAddress)

/**
 * createPaymentMethod
 *
 * @param {*} address
 */
export const createPaymentMethod = paymentMethod =>
  runMutation({
    mutation: CREATE_PAYMENT_METHOD,
    variables: {
      paymentMethod
    }
  }).then(({ data: { createPaymentMethod: paymentMethod } }) => paymentMethod)

/**
 * createPaymentIntent: ask Starbase to create a Stripe PaymentIntent for the
 * given DOLLAR amount and return { id, clientSecret } for confirming an Apple Pay
 * / Google Pay payment on the client.
 *
 * NOTE: `amount` is in DOLLARS. The server converts to cents
 * (starbase/services/stripe.js: Math.round(amount * 100)). Do NOT pre-multiply.
 * The intent is created for the authenticated user, so no userId is passed.
 *
 * @param {number} amount dollar amount to authorize
 * @returns {Promise<{ id: string, clientSecret: string }>}
 */
export const createPaymentIntent = amount =>
  runMutation({
    mutation: CREATE_PAYMENT_INTENT,
    variables: {
      amount
    }
  }).then(({ data: { createPaymentIntent: paymentIntent } }) => paymentIntent)

/**
 * destroyPaymentMethod
 *
 * @param {string} id
 */
export const destroyPaymentMethod = id => {
  runMutation({
    mutation: DESTROY_PAYMENT_METHOD,
    variables: {
      id
    }
  }).then(({ data: { destroyPaymentMethod } }) => destroyPaymentMethod)
}

/**
 * fetches a user
 *
 * @param {*} userId
 */
export const findUserById = userId =>
  runQuery({
    query: LOAD_USER,
    variables: { where: { id: userId } },
  }).then(({ data }) => data.users[0])

/**
 * fetches appointments for an authenticated user
 *
 *
 */
export const queryUserAppointments = () =>
  runQuery({
    query: LOAD_USER_APPOINTMENTS,
  }).then(({ data }) => data.me.appointments)

export const useMeAppointments = () =>
  useStateEffect(queryUserAppointments, undefined, [getAccessToken()])

export const updateAppointment = (id, newInfo) =>
  runMutation({
    mutation: UPDATE_APPOINTMENT,
    variables: {
      id,
      appointment: newInfo
    }
  }).then(({ data }) => data.updateAppointment)

/**
 * updateAppointmentService: persist a change to a single appointment service
 *
 * Takes the same `AppointmentServiceInput` as the pricing query, so the payload is
 * filtered through the same allowlist at the wire — callers may hand over a hydrated
 * service object without smuggling server-owned fields (`price`, `duration`,
 * `customPrice`) that the input type doesn't define for a client role.
 *
 * @param {string} id appointment service id
 * @param {object} newInfo fields to write (see APPOINTMENT_SERVICE_INPUT_FIELDS)
 * @returns {Promise<object>} the updated appointment service
 */
export const updateAppointmentService = (id, newInfo) =>
  runMutation({
    mutation: UPDATE_APPOINTMENT_SERVICE,
    variables: {
      id,
      appointmentService: _.pick(newInfo, APPOINTMENT_SERVICE_INPUT_FIELDS)
    }
  }).then(({ data }) => data.updateAppointmentService)

export const destroyAppointmentService = id =>
  runMutation({
    mutation: DESTROY_APPOINTMENT_SERVICE,
    variables: {
      id
    }
  }).then(
    ({ data: { destroyAppointmentService } }) => destroyAppointmentService
  )

/**
 *
 * @param {*} input SurveyAnswerInput
 * @returns
 */
// TEMPORARY: commented out for local survey testing — remove when done
export const createSurveyAnswer = surveyAnswer =>
  runMutation({
    mutation: CREATE_SURVEY_ANSWER,
    variables: { surveyAnswer }
  }).then(({ data: { createSurveyAnswer } }) => createSurveyAnswer)
// export const createSurveyAnswer = surveyAnswer => {
//   console.log('[Dev] createSurveyAnswer skipped:', surveyAnswer)
//   // Spread input back into the response to mirror what the real mutation returns.
//   // Without this, answers lack surveyQuestionId/appointmentServiceId and
//   // provider cycling can't match answers to questions for auto-advance.
//   return Promise.resolve({
//     ...surveyAnswer,
//     id: `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`,
//     resultingUserCredit: null,
//   })
// }

// TEMPORARY: commented out for local survey testing — remove when done
export const updateSurveyAnswer = surveyAnswer =>
  runMutation({
    mutation: UPDATE_SURVEY_ANSWER,
    variables: { id: surveyAnswer.id, surveyAnswer }
  }).then(({ data: { updateSurveyAnswer } }) => updateSurveyAnswer)
// export const updateSurveyAnswer = surveyAnswer => {
//   console.log('[Dev] updateSurveyAnswer skipped:', surveyAnswer)
//   return Promise.resolve({ ...surveyAnswer, resultingUserCredit: null })
// }

/**
 * saves and returns updated user
 *
 * @param {*} user
 * @returns
 */
export const updateUser = user =>
  runMutation({
    mutation: UPDATE_USER,
    variables: {
      id: user.id,
      user
    }
  }).then(({ data: { updateUser } }) => updateUser)

/**
 * runSubscriptionWatchedQuery: calls runQuery with args provided and subscribes to changes
 * in the type of resource returned and updates cache with any changed values
 *
 * NOTE: this does not subscribe to changes w/ a filter currently so it will receive EVERY
 * event for this type. use w/ caution
 *
 * @param {*} runQueryArgs
 * @returns
 */
const runSubscriptionWatchedQuery = runQueryArgs => {
  // we want to cache and use a subscription to update cache
  runQueryArgs.useCache = true

  return resolve(runQuery(runQueryArgs)).then(result => {
    (() => {
      const { query } = runQueryArgs

      // check if we're already watching this query
      const stringifiedQuery = JSON.stringify(query)
      if (watchedQueries[stringifiedQuery]) {
        return
      }
      watchedQueries[stringifiedQuery] = true

      // build the subcription query based on the original query
      const { root, fields, fragments } = getRootFieldsAndFragments(query)

      // construct event name to subscribe to from root selection
      const singularName = singular(root)
      const eventName = singularName + 'Event'

      // build base subscribe query
      const subscriptionQuery = gql`
          subscription {
            ${eventName} {
              ${singularName} {
                __typename
              }
            }
          }
        `

      // add fields from original query
      findNodeByName(
        subscriptionQuery,
        singularName
      ).selectionSet.selections.push(...fields)

      // add fragments if the query used any
      subscriptionQuery.definitions.push(...fragments)

      // updating the cache requires a typename for every type nested anywhere the query
      // so request the typename metafield for each
      const typenameField = {
        kind: 'Field',
        name: {
          kind: 'Name',
          value: '__typename'
        },
        arguments: [],
        directives: []
      }

      // find all selection lists in the doc and add typename
      callOnPropertiesWithName(fields, 'selections', node =>
        node.push(typenameField)
      )

      // NOTE: subscriptions disabled

      // subscribe
      // subscribe({
      //   query: subscriptionQuery,
      //   handler: ({ data }) => {
      //     const item = get(data, [eventName, singularName])

      //     // update cache w/ new/changed item received
      //     updateCache(query, item)
      //   }
      // })
    })()

    return result
  })
}

// queries we're currently watching w/ subscription
const watchedQueries = {}

/**
 * return names of root and fields and fragment definitions for gql query
 *
 * @param {*} document
 * @returns
 */
const getRootFieldsAndFragments = document => {
  const root = get(document, 'definitions.0.selectionSet.selections.0')

  return {
    root: get(root, 'name.value'),
    fields: get(root, 'selectionSet.selections'),
    fragments: document.definitions.filter(
      def => def.kind === 'FragmentDefinition'
    )
  }
}

/**
 * search gql query tree for attributes with a given name and call fn
 * passing the value. used to add __typename field to each query in a
 * subscription
 *
 * @param {*} node
 * @param {*} name
 * @param {*} fn
 * @returns
 */
const callOnPropertiesWithName = (node, name, fn) => {
  if (!node) {
    return
  }
  if (node[name]) {
    fn(node[name])
  }

  return values(node)
    .filter(o => typeof o === 'object')
    .flatMap(o => (isArray(o) ? o : [o]))
    .map(o => callOnPropertiesWithName(o, name, fn))
}

/**
 * finds a named node in a gql document
 *
 * @param {*} node
 * @param {*} name
 * @returns
 */
const findNodeByName = (node, name) => {
  if (get(node, 'name.value') === name) {
    return node
  }

  return values(node)
    .filter(o => typeof o === 'object')
    .flatMap(o => (isArray(o) ? o : [o]))
    .map(o => findNodeByName(o, name))
    .filter(o => !!o)[0]
}

/**
 * cachedLoadAndSubscribeFunctionsForQuery - returns functions which use provided query for loading
 * all of a resource, watch for changes via subscription and invalidate cache
 *
 * NOTE: listens for all changes to the type of resource requested and will receive EVERY
 * change event for that type
 *
 * @param {*} loadAllQuery
 *
 * @returns [
 *  loadAll,
 *  fetchOne,
 *  useAll,
 *  useOne,
 * ]
 */
const cachedLoadAndSubscribeFunctionsForQuery = (
  loadAllQuery,
  loadAllVariables
) => {
  const { root } = getRootFieldsAndFragments(loadAllQuery)

  // runs the provided query and watches for changes to the resource requested.
  //
  // callers may pass variables to narrow the query for a single call — used to scope
  // market services to one market. They replace the module-scope defaults key by key,
  // so a caller overriding one `where` must supply that whole clause.
  const loadAll = variables =>
    runSubscriptionWatchedQuery({
      query: loadAllQuery,
      variables: variables ? { ...loadAllVariables, ...variables } : loadAllVariables
    }).then(({ data }) => data[root])

  // function to fetch specific instance. just calls loadAll
  const fetchOne = id =>
    loadAll().then(results => results.find(o => o.id === id))

  const useAll = (deps = []) => useStateEffect(() => loadAll().catch(() => []), undefined, deps)
  const useOne = (id, deps = []) =>
    useStateEffect(() => fetchOne(id), undefined, [id, ...deps])

  return [loadAll, fetchOne, useAll, useOne]
}

/**
 * runs provided effect and stores result in state. returns the value of the state.
 * throws any errors returned
 *
 * @param {*} effect - passed to useEffect
 * @param {*} initialState - passed to useState
 * @param {*} [deps=[]] - passed to useEffect
 * @returns value
 */
const useStateEffect = (effect, initialValue, deps = []) => {
  // call the version that catches and returns errors
  const [value, error] = useStateEffectWithError(effect, initialValue, deps)
  // throw any error returned
  if (error) {
    throw Error(error)
  }

  return value
}

/**
 * runs provided effect and stores result in state.
 *
 * returns the value and error of the state
 *
 * @param {*} effect - passed to useEffect
 * @param {*} initialState - passed to useState
 * @param {*} [deps=[]] - passed to useEffect
 * @returns [value, error]
 */
export const useStateEffectWithError = (effect, initialValue, deps = []) => {
  if (!effect) {
    throw new Error('useStateEffectWithError: missing param')
  }

  const [{ value, error, retry }, setState] = useState({
    value: initialValue,
    error: undefined,
    retry: null
  })

  const setStateValue = value => {
    if (value && value?.retry) {
      setState({ value: value.data, error: undefined, retry: value.retry })
    } else {
      setState({ value, error: undefined })
    }
  }
  const setStateError = error => setState({ value: undefined, error })

  useEffect(
    () => {
      setState({ value: undefined, error: undefined }) // reset existing state

      // The first GraphQL reads on /book (markets and services) cannot render a
      // useful page without their data. Keep the loading state visible while a
      // short network interruption clears instead of immediately throwing into
      // the Page error boundary.
      let cancelled = false
      // `attempt` counts failures in the normal three-attempt budget. The
      // diagnostic recovery attempt is tracked separately so it can happen at
      // most once and can never form an unbounded retry loop.
      let attempt = 0
      let retryTimer = null
      let recoveryRetryUsed = false
      const run = async () => {
        if (cancelled) {
          return
        }
        retryTimer = null
        try {
          const result = await resolve(effect())
          if (!cancelled) {
            setStateValue(result)
          }
        } catch (err) {
          if (cancelled) {
            return
          }
          attempt += 1

          const isTransient = isTransientLoadError(err)
          if (isTransient && attempt < STATE_EFFECT_MAX_ATTEMPTS) {
            // Two quick backoffs (400 ms, then 1.2 s) cover ordinary dropped
            // requests without making a persistent outage feel indefinite.
            retryTimer = setTimeout(run, STATE_EFFECT_RETRY_DELAYS_MS[attempt - 1])
            return
          }
          if (!isTransient || recoveryRetryUsed) {
            setStateError(err)
            return
          }

          // All normal attempts failed. Keep the loading state up while the
          // short reachability probes run. The module stays lazy because a
          // static import would create a helpers ⇄ services cycle.
          let diagnostics = null
          try {
            const { reportStarbaseUnreachable } = await import('../helpers/networkDiagnostics')
            diagnostics = await reportStarbaseUnreachable({ context: 'state-effect', error: err })
          } catch {
            // Diagnostics must never replace or hide the original load error.
          }
          if (cancelled) {
            return
          }

          if (diagnostics?.verdict === 'recovered') {
            // The API answered the probe, so make one immediate final read. If
            // it fails, `recoveryRetryUsed` sends the original flow to the error
            // boundary without probing or retrying again.
            recoveryRetryUsed = true
            await run()
            return
          }
          setStateError(err)
        }
      }
      run()
      return () => {
        // unmount / deps change: no more attempts, no state updates, no timer
        cancelled = true
        if (retryTimer) {
          clearTimeout(retryTimer)
          retryTimer = null
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps
  )

  if (retry) {
    return [value, error, retry]
  }

  return [value, error]
}

/** Total attempts (first try + retries) useStateEffectWithError makes for a transient failure. */
export const STATE_EFFECT_MAX_ATTEMPTS = 3
/** Back-off before the 2nd and 3rd attempts. */
export const STATE_EFFECT_RETRY_DELAYS_MS = [400, 1200]

/**
 * isTransientLoadError: does this error look like a request that never
 * completed (network drop, aborted connection), as opposed to a server
 * response we should not retry (GraphQL validation, 4xx)? Matches the wording
 * every major browser uses for a failed fetch.
 */
export const isTransientLoadError = err => {
  const message = String(err?.message || err || '')
  return /Failed to fetch|Load failed|Network ?Error|NetworkError|ECONNRESET|timeout/i.test(message)
}

/**
 * Markets
 */
export const [loadMarkets, fetchMarket, useAllMarkets, useMarket] =
  cachedLoadAndSubscribeFunctionsForQuery(LOAD_MARKETS)

/**
 * MarketServices
 *
 * Base filter for the bookable, non-add-on market services the booking flow renders.
 * Exported shape is reused by useAllMarketServices when it adds a marketId, so the two
 * cannot drift.
 */
const MARKET_SERVICES_WHERE = {
  isAddOn: false,
  isHidden: false,
  isClientBookable: true
}

/** Base filter for the pure add-ons hung off each market service. */
const ADD_ON_MARKET_SERVICES_WHERE = {
  isPureAddOn: true,
  isClientBookable: true,
  isHidden: false
}

export const [
  loadMarketServices,
] = cachedLoadAndSubscribeFunctionsForQuery(LOAD_MARKET_SERVICES_WITH_ADD_ONS, {
  marketServicesWhere: MARKET_SERVICES_WHERE,
  addOnMarketServicesWhere: ADD_ON_MARKET_SERVICES_WHERE
})

/**
 * ServiceTypes
 */
export const [
  loadServiceTypes,
  fetchServiceType,
  useAllServiceTypes,
  useServiceType
] = cachedLoadAndSubscribeFunctionsForQuery(SERVICE_TYPES)

/**
 * Services
 */
export const [loadServices, fetchService, useAllServices, useService] =
  cachedLoadAndSubscribeFunctionsForQuery(LOAD_SERVICES, { where: { $or: [{ isAddOn: false }, { isPureAddOn: true }] }})

/**
 * useAllMarketServices: one market's market services with services and service types populated
 *
 * loads market services, services and service types in parallel then populates the
 * services and service types on the market services
 *
 * The marketServices query MUST be scoped to a single market. Starbase caps the
 * marketServices resolver at 500 rows, and the unscoped query asks for every market at
 * once (~1,500 rows across 22 markets, disabled ones included). The overflow is dropped
 * silently — no error, no partial-result flag — so whichever services sort past the cap
 * simply never reach the booking flow. That is how the Halloween makeup services went
 * missing from step two while still appearing on /services, which queries one market.
 * Every caller passes the market it is rendering; there is deliberately no unscoped path.
 *
 * @param {string} [marketId] market to load; undefined until one is selected
 * @returns {[MarketService]|undefined} undefined while no market is selected or the
 *   fetch is in flight
 */
export const useAllMarketServices = marketId => useStateEffect(
  async () => {
    if (!marketId) {
      return undefined
    }

    const [marketServices, services, serviceTypes] = await Promise.all([
      loadMarketServices({
        marketServicesWhere: { ...MARKET_SERVICES_WHERE, marketId }
      }),
      loadServices(),
      loadServiceTypes(),
    ])
      .then(([marketServices, services, serviceTypes]) => [cloneDeep(marketServices), cloneDeep(services), cloneDeep(serviceTypes)])

    if (!marketServices || !services || !serviceTypes) {
      return []
    }

    // key services and service types by id for easy lookup
    const servicesById = _.keyBy(services, 'id')
    const serviceTypesById = _.keyBy(serviceTypes, 'id')

    // populate service types on services
    services.forEach(s => {
      s.serviceType = serviceTypesById[s.serviceTypeId]
    })

    // populate services on market services
    marketServices.forEach((marketService) => {
      marketService.service = servicesById[marketService.serviceId]

      // populate services on add-on market services
      marketService.addOnMarketServices.forEach((addOnMarketService) => {
        addOnMarketService.service = servicesById[addOnMarketService.serviceId]
      })

      // filter out any add-on market services w/o services
      marketService.addOnMarketServices = marketService.addOnMarketServices.filter(ams => ams.service)
    })

    // return populated market services, filtering out any w/o services
    return marketServices.filter(ms => ms.service);
  },
  undefined,
  // refetch when the market changes — the query is scoped to one market now
  [marketId]
);

/**
 * Products
 */
export const [loadProducts, fetchProduct, useAllProducts, useProduct] =
  cachedLoadAndSubscribeFunctionsForQuery(PRODUCTS)

/**
 * Memberships
 */
export const [
  loadMemberships,
  fetchMembership,
  useAllMemberships,
  useMembership
] = cachedLoadAndSubscribeFunctionsForQuery(LOAD_MEMBERSHIPS)

/**
 * retrieve a user's addresses
 *
 * @param {*} userId
 */
export const getUserAddresses = userId =>
  runQuery({
    query: ADDRESSES,
    variables: { where: { userId } }
  }).then(({ data: { addresses } }) => addresses)

/**
 * retrieve a user's payment methods
 *
 * @param {*} userId
 */
export const getUserPaymentMethods = userId =>
  createClient()
    .query({
      query: PAYMENT_METHODS,
      variables: { where: { userId: userId } }
    })
    .then(({ data: { paymentMethods } }) => paymentMethods)

/**
 * checkout an order
 *
 * @param {*} order
 */
export const checkoutOrder = orderInfo => {
  return runMutation({
    mutation: CREATE_TRANSACTION,
    variables: {
      transaction: orderInfo
    }
  }).then(
    ({
      data: {
        createTransaction: { id }
      }
    }) => id
  ).catch(formatAndRethrowError)
}

/**
 * fetch a specific appointment along with owner, appointment service,
 * address and location information
 *
 * @param {*} id
 */
export const fetchAppointment = id =>
  runQuery({
    query: APPOINTMENT_DETAILS,
    variables: {
      appointmentId: id
    }
  }).then(({ data: { appointment } }) => appointment)

/**
 * fetch a specific appointment along with owner, appointment service,
 * address and location information
 *
 * @param {*} id
 */
export const fetchAppointmentService = id =>
  runQuery({
    query: APPOINTMENT_SERVICE_DETAILS,
    variables: {
      id
    }
  }).then(({ data: { appointment } }) => appointment)

/**
 * loads packages for a specific market id
 *
 *
 * @param {*} marketId
 */
export const fetchMarketPackages = marketId =>
  runQuery({
    query: LOAD_PACKAGES,
    variables: {
      marketId
    }
  }).then(({ data: { market } }) =>
    sortBy(market.products.filter(product => product.productGroup?.isEnabled), 'productGroup.priority')
  )

/**
 * loads services for a specific market id
 * weddings not included for services page
 *
 * @param {*} marketId
 */

export const fetchServicesForMarket = marketId =>
  runQuery({
    query: LOAD_SERVICES_FOR_MARKET,
    variables: {
      id: marketId
    }
  }).then(
    ({
      data: {
        market: { marketServices }
      }
    }) => {
      return _(marketServices)
        .sortBy(s => s.service.priority)
        .map(s => ({
          ...s,
          addOnMarketServices: sortBy(s.addOnMarketServices, 'service.priority')
        }))
        .groupBy(s => s.service.serviceType.name)
        .value()
    }
  )

/**
 * Loads all base services with the full field selection the service detail pages render.
 * Market-independent — page content does not depend on a market. Returns a flat array sorted
 * by service priority.
 * @returns {Promise<Array<object>>}
 */
export const fetchDetailServices = () =>
  runQuery({ query: LOAD_DETAIL_SERVICES }).then(({ data: { services } }) =>
    sortBy(services, s => s.priority)
  )

export const fetchEventServicesForMarket = (marketId) =>
  runQuery({
    query: LOAD_SERVICES_FOR_MARKET,
    variables: {
      id: marketId,
    },
  })
    .then(
      ({
        data: {
          market: { marketServices },
        },
      }) => {
        return _(marketServices)
          .filter(
            (s) =>
              s.service.serviceTypeId ===
              process.env.NEXT_PUBLIC_EVENT_SERVICE_TYPE_ID,
          )
          .sortBy((s) => s.service.priority)
          .value()
      },
    )
    .catch(formatAndRethrowError)

export const fetchGiftCard = () =>
  runQuery({
    query: LOAD_GIFT_CARD
  }).then(({ data: { products } }) => products[0])

/**
 *
 * fetch question groups and questions for client post-appointment survey
 * @returns
 */

export const fetchClientSurveyQuestions = () =>
  runQuery({
    query: LOAD_CLIENT_SURVEY_QUESTIONS,
  }).then(({
    data: {
      surveyQuestionGroups
    }
  }) => surveyQuestionGroups)
    .filter(g => g.by === 'USER')

/**
 * react hook which runs a query and calls handler with results
 *
 * @param {*} query
 * @param {*} variables
 * @param {*} handler
 * @param {*} [values=[]]
 * @returns
 */
export const useQuery = (query, variables, handler, values = []) =>
  useEffect(() => {
    runQuery({ query, variables }).then(({ data }) => handler(data))
  }, [query, variables, handler, values])

/**
 * react hook which subscribes with a query and calls handler as events arrive
 *
 * @param {*} query
 * @param {*} variables
 * @param {*} handler
 * @param {*} [values=[]]
 * @returns
 */
export const useSubscription = (query, filter, handler, values = []) =>
  useEffect(
    () => subscribe({ query, variables: { filter }, handler }).close,
    [query, filter, handler, values]
  )

/**
 * runs a query
 *
 * @param {*} {
 *   query,
 *   variables = {},
 *   useCache = false,
 *   requestId,
 * }
 * @returns Promise
 */
export const runQuery = ({
  query,
  variables = {},
  useCache = false,
  requestId
}) => {
  const client = createClient()
  return resolve()
    .then(() => client.query({
      query,
      variables,
      fetchPolicy: useCache ? 'cache-first' : 'network-only'
    }))
    .then(res => {
      const errors = res.data.errors
      if (errors && errors.length) {
        throw new Error(errors.map(e => e.message))
      }

      const data = res.data

      return { data, requestId }
    })
    .catch(formatAndRethrowError)
}

/**
 * runs a mutation
 *
 * @param {*} {
 *   mutation,
 *   variables = {},
 *   requestId,
 * }
 * @returns Promise
 */
export const runMutation = ({ mutation, variables = {}, requestId }) => {
  const client = createClient()

  return resolve()
    .then(() => client.mutate({
      mutation,
      variables
    }))
    .then(({ data }) => {
      const errors = data.errors
      if (errors && errors.length) {
        throw new Error(errors.map(e => e.message))
      }

      return { data, requestId }
    })
    .catch(err => {
      formatAndRethrowError(err)
    })
}

// the subscription client
let subscriptionClient

/**
 * subscribe to a query with a handler
 *
 * @param {*} { query, variables, handler }
 * @returns
 */
export const subscribe = async ({ query, variables, handler }) => {
  const accessToken = getAccessToken()

  // connect if not already connected
  if (!subscriptionClient) {
    const protocol = window.location.protocol === 'http:' ? 'ws://' : 'wss://'

    const subscriptionEndpoint =
      STARBASE_URL.replace(/^https?:\/\//i, protocol) + '/api/v1/graphql'

    // const subscriptionEndpoint = window.location.protocol === 'http:'
    //   ? `ws://${STARBASE_URL}/api/v1/graphql`
    //   : `wss://${STARBASE_URL}/api/v1/graphql`

    subscriptionClient = new SubscriptionClient(subscriptionEndpoint, {
      reconnect: true,
      connectionParams: { authorization: accessToken }
    })

    // reconnect periodically
    setInterval(() => subscriptionClient.close(false), 60 * 1000)
  }

  // request and subscribe w/ handler
  const { unsubscribe } = subscriptionClient
    .request({ query, variables })
    .subscribe({ next: handler })

  return {
    // return function to unsubscribe
    close: unsubscribe
  }
}

// apollo client and cache
let client

const cache = new InMemoryCache()

// last access token. used to clear cache when changed
let previousAccessToken

/**
 * Longest GET URL we let a GraphQL query travel as. CloudFront (in front of
 * Starbase) rejects URLs over 8,192 bytes with a 414 that carries no CORS
 * headers, which the browser can only report as `TypeError: Failed to fetch`
 * (Chromium) / `Load failed` (WebKit). Large carts hit this: every appointment
 * service carries a ~1.4 KB `bookingToken`, so six services in
 * `calculateAppointmentPrice` is already ~10 KB of URL. Kept comfortably under
 * the limit to leave room for the host, headers Apollo appends, and encoding.
 */
export const MAX_GET_URL_LENGTH = 7500

/**
 * `fetch` for the Apollo HttpLink. Queries normally go out as GETs
 * (`useGETForQueries`) so CloudFront can cache the catalog responses, which
 * carry `cache-control: public, max-age=300`. When the GET URL would exceed
 * MAX_GET_URL_LENGTH the same operation is re-sent as a JSON POST to the bare
 * endpoint instead, which has no meaningful size limit. Anything that isn't
 * an oversized GET is passed straight through.
 *
 * @param {string|URL|Request} uri
 * @param {RequestInit} options
 * @returns {Promise<Response>}
 */
export const fetchWithPostFallback = (uri, options = {}) => {
  const method = (options.method || 'GET').toUpperCase()
  const url = typeof uri === 'string' ? uri : uri?.url || String(uri)

  if (method !== 'GET' || url.length <= MAX_GET_URL_LENGTH) {
    return fetch(uri, options)
  }

  const [endpoint, search = ''] = url.split('?')
  const params = new URLSearchParams(search)
  const body = {}
  try {
    for (const key of ['operationName', 'query', 'variables', 'extensions']) {
      const value = params.get(key)
      if (value === null) {
        continue
      }
      body[key] = key === 'query' || key === 'operationName' ? value : JSON.parse(value)
    }
  } catch {
    // malformed params we can't rebuild: let the GET go out and fail loudly
    // rather than sending a broken POST
    return fetch(uri, options)
  }

  return fetch(endpoint, {
    ...options,
    method: 'POST',
    headers: { ...(options.headers || {}), 'content-type': 'application/json' },
    body: JSON.stringify(body)
  })
}

/**
 * create an apollo client w/ caching for queries & mutations
 *
 * @returns
 */
const createClient = () => {
  const accessToken = getAccessToken()

  // return same client unless token has changed
  if (!client) {
    previousAccessToken = accessToken

    const headers = {}
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`
    }

    const httpLink = createHttpLink({
      uri: STARBASE_URL.concat('/api/v1/graphql'),
      headers,
      useGETForQueries: true,
      // oversized GETs are re-sent as POSTs; see fetchWithPostFallback
      fetch: fetchWithPostFallback
    })

    client = new ApolloClient({
      link: httpLink,
      cache
    })
  }

  return client
}

/**
 * clears the apollo cache
 *
 */
const clearCache = () => {
  cache.data.clear()
}

/**
 * retrieve the current access token from session/local storage
 *
 * @returns
 */
export const getAccessToken = () => {
  const localstorage = storageMode('localStorage')
  const sessionstorage = storageMode('sessionStorage')
  const accessToken =
    localstorage.getItem('accessToken') || sessionstorage.getItem('accessToken')
  if (accessToken !== previousAccessToken) {
    // token changed, clear the cache
    clearCache()
    client = null
  }
  previousAccessToken = accessToken

  return accessToken
}

/**
 * attempt to format server errors received in various forms
 *
 * @param {*} err
 */
export const formatAndRethrowError = err => {
  console.dir(err)
  const errors = chain([])
    .concat(get(err, 'graphQLErrors', []).map(err => err.message))
    .concat(get(err, 'errors', []).map(err => err.message))
    .concat(get(err, 'networkError.result.errors', []).map(err => err.message))
    .concat(get(err, 'message'))
    .compact() // removes falsey values from array
    .map(msg => msg.replace('GraphQL error: ', ''))
    .uniq()
    .valueOf()
    .join('. ')
    .replace(/GraphQL error:\s/g, '')
    .replace('null: ', '')
    .trim()

  if (errors && errors.length) {
    err.message = errors
  }

  throw err
}

/**
 * locateMarketForAddress: returns the market associated with provided address, if any
 *
 * @param {*} address
 * @returns
 */
export const locateMarketForAddress = address => {
  const accessToken = getAccessToken()

  return fetch(STARBASE_URL.concat('/api/v1/markets/address-lookup'), {
    method: 'POST',
    headers: {
      Authorization: accessToken ? 'Bearer '.concat(accessToken) : undefined,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(address),
    timeout: 15000,
    responseType: 'json'
  })
    .then(res => res.json())
    .then(({ data }) => data)
    .catch(formatAndRethrowError)
}


export const fetchAppointments = (userId, limit, offset) => {

  const variables = {
    where: {
      ownerId: userId,
    },
    limit,
    offset
  }

  runQuery({
    query: LOAD_APPOINTMENTS,
    variables
  }).then(({ data }) => data)
    .catch(e => formatAndRethrowError(e))
}

export const fetchUpcomingAppointments = (userId) => {

  const variables = {
    where: {
      ownerId: userId,
      actualStartDateTime: { gte: moment().toISOString() },
      isCanceled: false
    },
    limit: 200,
    offset: 0,
    order: 'actualStartDateTime'
  }

  return runQuery({
    query: LOAD_APPOINTMENTS,
    variables
  }).then(({ data: { appointments } }) => appointments)
    .catch(e => console.log(e))
}

export const fetchPastAppointments = (userId, limit, offset) => {
  const variables = {
    where: {
      ownerId: userId,
      actualStartDateTime: { lt: moment().toISOString() },
      isCanceled: false
    },
    limit,
    offset,
    order: 'reverse:actualStartDateTime'
  }

  return runQuery({
    query: LOAD_APPOINTMENTS,
    variables
  }).then(({ data: { appointments } }) => appointments)
    .catch(e => console.log(e))
}

export const fetchSingleProduct = (id) =>
  runQuery({
    query: FETCH_PRODUCT,
    variables: { id }
  }).then(({ data: { product } }) => product)
    .catch(formatAndRethrowError)

export const getBanner = async () => {
  try {
    const { data: { getBanner } } = await runQuery({
      query: GET_BANNER,
      variables: {
        clientApplicationId: CLIENT_APPLICATION_ID
      }
    })
    return getBanner
  } catch (e) {
    formatAndRethrowError(e)
  }
}

export const updateMembership = (id, membership) => {
  return runMutation({
    mutation: UPDATE_MEMBERSHIP,
    variables: {
      id,
      membership
    }
  }).then(({ data: { updateMembership } }) => updateMembership)
    .catch(formatAndRethrowError)
}
